import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Sparkles, Award, Users, Globe, ArrowRight } from "lucide-react";
import { api, BACKEND_URL } from "../lib/api";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

export default function About() {
  const [directors, setDirectors] = useState([]);
  useEffect(() => {
    api.get("/directors").then((r) => setDirectors(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative bg-[#1F3864] text-white py-28 sm:py-36 overflow-hidden" data-testid="about-hero">
        <img src="https://images.unsplash.com/photo-1756136836696-0e3374ebfbac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">About Next Gen Praise NPC</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            We are raising a generation that praises <span className="italic text-[#C00000]">beyond limits.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center gap-3 text-[#C00000] mb-4">
              <Target className="w-5 h-5" /><span className="text-xs tracking-[0.3em] uppercase">Our Mission</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-6 leading-tight">To raise, equip and release the next generation of gospel artists.</h2>
            <p className="text-gray-700 leading-relaxed">
              We exist to bridge the gap between anointed creatives and the commercial knowledge they need to
              sustain lifelong ministry. Through content, community and coaching, we equip worshippers with the
              tools to thrive — spiritually and professionally.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3 text-[#C00000] mb-4">
              <Eye className="w-5 h-5" /><span className="text-xs tracking-[0.3em] uppercase">Our Vision</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-6 leading-tight">A continent where every worshipper is empowered to praise without limit.</h2>
            <p className="text-gray-700 leading-relaxed">
              Africa is the worship frontier. Our vision is a movement rooted in Durban but unrestrained by
              geography — producing gospel artists who minister with excellence, authenticity and integrity
              across the continent and the world.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-20 sm:py-28" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Our Values</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-12 max-w-2xl leading-tight">Four convictions that shape everything we do.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: "Anointing", desc: "Every note, every frame, every gathering must carry the presence of God." },
              { icon: Award, title: "Excellence", desc: "We refuse the false choice between anointing and excellence. Both or neither." },
              { icon: Users, title: "Community", desc: "No artist rises alone. We build in family — not in factories." },
              { icon: Globe, title: "Africa-First", desc: "The world is looking to Africa. We will not be a copy. We are an original." },
            ].map((v) => (
              <div key={v.title} className="bg-white p-8 border-t-4 border-[#C00000]">
                <v.icon className="w-8 h-8 text-[#1F3864] mb-5" />
                <h3 className="font-display text-2xl text-[#1F3864] mb-3">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="about-directors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Leadership</div>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-12 max-w-2xl leading-tight">Meet the directors.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {directors.map((d) => (
              <div key={d.id || d.name}>
                <div className="aspect-[4/5] bg-gray-100 overflow-hidden mb-5">
                  <img src={resolveUrl(d.photo_url)} alt={d.name} className="w-full h-full object-cover" />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2">{d.role}</div>
                <h3 className="font-display text-2xl text-[#1F3864] mb-3">{d.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{d.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1F3864] text-white py-20 sm:py-24" data-testid="about-registration">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Legal & Compliance</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-6 leading-tight">Registered. Compliant. Accountable.</h2>
            <p className="text-white/70 leading-relaxed">
              Next Gen Praise NPC is a registered South African non-profit company. We operate with full
              financial transparency and comply with all SARS and POPIA requirements.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="border-l-2 border-[#C00000] pl-5">
              <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-2">Registration</div>
              <div className="font-display text-2xl">2026/237461/08</div>
            </div>
            <div className="border-l-2 border-[#C00000] pl-5">
              <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-2">Entity Type</div>
              <div className="font-display text-2xl">NPC</div>
            </div>
            <div className="border-l-2 border-[#C00000] pl-5">
              <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-2">Tax Status</div>
              <div className="font-display text-2xl">PBO (Applied)</div>
            </div>
            <div className="border-l-2 border-[#C00000] pl-5">
              <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-2">B-BBEE</div>
              <div className="font-display text-2xl">Level 1</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 text-center" data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-4xl lg:text-5xl text-[#1F3864] mb-6 leading-tight">Want to partner or collaborate?</h2>
          <p className="text-gray-600 mb-8">We are always open to conversations with ministries, artists, sponsors and venues.</p>
          <Link to="/contact" className="btn-primary-red" data-testid="action-about-contact-button">
            Get In Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
